import { ApolloClient, HttpLink, InMemoryCache, ApolloLink, from, split } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { WebSocketLink } from "@apollo/client/link/ws";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { clearStorage, generateNonce, getJwtPromise, getLastDocumentReferrer, getParameterByName, getWeEAMUrl, isValidReferrer } from "../main/OauthUtils";
import GraphQLQueryLogger from "../interceptors/RequestLogger";

const TIME_OUT_LINK = 800_000;

// Dynamically set URLs
const GRAPHQL_HTTP_URL = `${process.env.REACT_APP_GRAPHQL_PROTOCOL}://${process.env.REACT_APP_GRAPHQL_SERVER}`;
const GRAPHQL_WS_URL = `${process.env.REACT_APP_GRAPHQL_WS_URL}`;
const GRAPHQL_WS_ENABLED = process.env.REACT_APP_GRAPHQL_WS_ENABLED === "true";

const resolveGraphqlWsUrl = () => {
    if (GRAPHQL_WS_URL && GRAPHQL_WS_URL !== "undefined" && GRAPHQL_WS_URL !== "null") {
        if (GRAPHQL_WS_URL.startsWith("http://")) {
            return GRAPHQL_WS_URL.replace("http://", "ws://");
        }
        if (GRAPHQL_WS_URL.startsWith("https://")) {
            return GRAPHQL_WS_URL.replace("https://", "wss://");
        }
        return GRAPHQL_WS_URL;
    }

    return GRAPHQL_HTTP_URL.replace(/^http/, "ws");
};

function fetchWithTimeout(uri: RequestInfo | URL, options: RequestInit = {}): Promise<Response> {
    return new Promise<Response>((resolve, reject) => { // ✅ Explicitly return a Promise<Response>
        const timer = setTimeout(() => {
            reject(new Error('Request timed out.'));
        }, TIME_OUT_LINK);

        fetch(uri, options)
            .then((response) => {
                clearTimeout(timer);
                resolve(response);
            })
            .catch((err) => {
                clearTimeout(timer);
                reject(err);
            });
    });
}

// Create HTTP link
const httpLink = new HttpLink({ uri: GRAPHQL_HTTP_URL,
    fetch: (uri, options) => {
        return fetchWithTimeout(uri, options);
    }
});

// Create WebSocket link only when explicitly enabled
const wsLink = GRAPHQL_WS_ENABLED
    ? new WebSocketLink(
        new SubscriptionClient(resolveGraphqlWsUrl(), { reconnect: true, lazy: true })
    )
    : null;

// Middleware for Authorization
const authMiddleware = new ApolloLink((operation, forward) => {
    const accessToken = localStorage.getItem("access_token");
    const idToken = localStorage.getItem("id_token");
    const username = getJwtPromise()?.sub || localStorage.getItem("username");
    const userId = localStorage.getItem("userId");

    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            authorization: accessToken ? `Bearer ${accessToken}` : null,
            username,
            userId,
            token: accessToken,
            idToken,
            authentication: idToken ? `Bearer ${idToken}` : null,
        }
    }));

    new GraphQLQueryLogger(username).logGraphQL(operation);
    return forward(operation);
});
// Error handling
const authorizationErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
    let had401AuthError = false;
    let had403AuthError = false;
    const skip401Logout = Boolean(operation.getContext()?.skip401Logout);

    if (graphQLErrors) {
        graphQLErrors.forEach(({ message }) => {
            console.error(`[GraphQL error]: ${message}`);
            if (message.includes("401")) had401AuthError = true;
            if (message.includes("403")) had403AuthError = true;
        });
    }

    if (networkError) {
        console.error(`[Network error]: ${networkError}`);
        const statusCode = (networkError as any)?.statusCode || (networkError as any)?.status;
        if (statusCode === 401) had401AuthError = true;
    }

    if (had401AuthError) {
        if (skip401Logout) {
            console.warn(`Skipping global logout for operation ${operation.operationName || "unknown-operation"} after 401 response.`);
            return;
        }

        const validReferrer = isValidReferrer(getLastDocumentReferrer());
        const realm = getParameterByName("realm");
       

        if (validReferrer || realm) {
            getWeEAMUrl().then((url) => {
                try{
                    const parsed = new globalThis.URL(url.toString());
                    const trustedHosts = [ "bmwgroup.net", "bmwgroup.com", "bmw.com", "bmw.net"];

                    const isTrustedHost = trustedHosts.some(root =>
                        parsed.hostname === root || parsed.hostname.endsWith(`.${root}`)
                    );
                    if (!isTrustedHost) {
                        console.error("BLOCKED REDIRECT — Untrusted URL:", parsed.hostname);
                        return; 
                    }
                // Append nonce securely
                parsed.searchParams.append("nonce", generateNonce("xxx"));
                // Get the final URL
                const finalUrl = parsed.toString();
                console.log("Redirecting to:", finalUrl);
                // Redirect
                window.location.assign(finalUrl);
                }
                catch(e){
                        console.error("Invalid redirect URL:", e);
                    };

            });

        } else {
            console.error("Token expired - Logging out");
            if (window.location.pathname !== "/logout") {
                clearStorage();
                sessionStorage.setItem("sessionExpired", "true");
                window.location.href = "/logout";
            }
        }
    }

    if (had403AuthError) {
        console.error("FORBIDDEN - User authenticated but not allowed access.");
    }
});

// Use split to choose between WebSocket and HTTP only when WS is enabled
const operationLink = wsLink
    ? split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === "OperationDefinition" && definition.operation === "subscription";
        },
        wsLink,
        httpLink
    )
    : httpLink;

// Apollo Client instance
const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authorizationErrorLink, authMiddleware, operationLink]),
});

export default client;
